import React, { createContext, useContext, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import './App.css';
import { installAxiosErrorInterceptor } from './utils/globalErrorHandler';

// Install ONCE per page-load — surfaces every unhandled API failure as a
// visible red toast with copy-to-clipboard for support hand-off.
installAxiosErrorInterceptor(axios);

// Pages
import LandingPage from './pages/LandingPage';
import AdminLogin from './pages/AdminLogin';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/Dashboard';
import KioskList from './pages/KioskList';
import KioskDetail from './pages/KioskDetail';
import ContactsList from './pages/ContactsList';
import AdminUsers from './pages/AdminUsers';
import KioskApp from './pages/KioskApp';
import AnalyticsDashboard from './pages/AnalyticsDashboard';
import PhotoDownload from './pages/PhotoDownload';
import PrivacyPolicy from './pages/PrivacyPolicy';
import SoftwareDownload from './pages/SoftwareDownload';
import Vouchers from './pages/Vouchers';
import AuditLog from './pages/AuditLog';
import FleetDashboard from './pages/FleetDashboard';
import LatePrints from './pages/LatePrints';
import Payments from './pages/Payments';
import TestDataCleanup from './pages/TestDataCleanup';
import LogsCenter from './pages/LogsCenter';
import ImportHistory from './pages/ImportHistory';
import PartnerDashboard from './pages/PartnerDashboard';
import { PushToggle } from './components/PushToggle';
import { registerSW } from './push';
import BoothOnly from './components/BoothOnly';
import { Toaster } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// true when the user may perform write actions in the given module
export const canEdit = (user, module) =>
  user?.role === 'super_admin' || user?.permissions?.[module] === 'edit';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('piczoo_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Per Emergent Auth playbook: skip the /auth/me check when we're
    // returning from the OAuth redirect (URL has #session_id=...) — the
    // AuthCallback route handles that flow and will set the token itself.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    const verifyToken = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (e) {
          localStorage.removeItem('piczoo_token');
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    verifyToken();
  }, [token]);

  // Called by AuthCallback after the OAuth redirect delivers session_id.
  // The backend exchanges session_id -> Piczoo JWT and enforces the
  // whitelist. We just store the resulting JWT locally.
  const loginWithSessionId = async (sessionId) => {
    const response = await axios.post(
      `${API}/auth/google/session`,
      {},
      { headers: { 'X-Session-ID': sessionId } }
    );
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('piczoo_token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('piczoo_token');
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, loginWithSessionId, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Ordered fallback: first module the user can access decides their landing page
const PERM_ROUTE_ORDER = [
  ['dashboard', '/admin/dashboard'],
  ['analytics', '/admin/analytics'],
  ['booths', '/admin/kiosks'],
  ['payments', '/admin/payments'],
  ['vouchers', '/admin/vouchers'],
  ['leads', '/admin/contacts'],
  ['software', '/admin/software'],
];

const firstAllowedPath = (user) => {
  if (!user) return null;
  if (user.role === 'partner') return '/partner';
  if (user.role === 'super_admin') return '/admin/dashboard';
  for (const [perm, path] of PERM_ROUTE_ORDER) {
    const level = user.permissions?.[perm];
    if (level && level !== 'none') return path;
  }
  return null;
};

const NoAccess = () => (
  <div className="min-h-screen bg-cream-50 flex items-center justify-center p-6" data-testid="no-access-page">
    <div className="text-center max-w-sm">
      <h2 className="font-serif text-2xl text-charcoal-900 mb-2">No access</h2>
      <p className="text-charcoal-800/60 text-sm">Your account has no modules enabled. Ask a super admin to grant you permissions.</p>
    </div>
  </div>
);

// Protected Route
const ProtectedRoute = ({ children, requirePermission, requireSuperAdmin, partnerOnly }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-cream-50 flex items-center justify-center">
        <div className="animate-pulse text-charcoal-900 font-serif text-2xl">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  // Partners only ever see /partner; admins never see /partner.
  if (user.role === 'partner' && !partnerOnly) {
    return <Navigate to="/partner" replace />;
  }
  if (partnerOnly && user.role !== 'partner') {
    const fallback = firstAllowedPath(user);
    return fallback ? <Navigate to={fallback} replace /> : <NoAccess />;
  }

  if (requireSuperAdmin && user.role !== 'super_admin') {
    const fallback = firstAllowedPath(user);
    return fallback ? <Navigate to={fallback} replace /> : <NoAccess />;
  }

  if (requirePermission && user.role !== 'super_admin') {
    const level = user.permissions?.[requirePermission];
    if (!level || level === 'none') {
      const fallback = firstAllowedPath(user);
      if (fallback && fallback !== location.pathname) {
        return <Navigate to={fallback} replace />;
      }
      return <NoAccess />;
    }
  }

  return children;
};

// Admin Layout
const AdminLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const allItems = [
    { path: '/admin/dashboard', label: 'Dashboard', perm: 'dashboard' },
    { path: '/admin/fleet', label: 'Fleet', perm: 'booths', superHint: true },
    { path: '/admin/kiosks', label: 'Booths', perm: 'booths' },
    { path: '/admin/analytics', label: 'Analytics', perm: 'analytics' },
    { path: '/admin/vouchers', label: 'Vouchers', perm: 'vouchers' },
    { path: '/admin/payments', label: 'Payments', perm: 'payments' },
    { path: '/admin/late-prints', label: 'Late Prints', perm: 'payments' },
    { path: '/admin/contacts', label: 'Leads', perm: 'leads' },
    { path: '/admin/software', label: 'Software', perm: 'software' },
  ];

  const isSuper = user?.role === 'super_admin';
  const navItems = allItems.filter((item) => {
    if (isSuper) return true;
    const level = user?.permissions?.[item.perm];
    return level && level !== 'none';
  });

  if (isSuper) {
    navItems.push({ path: '/admin/users', label: 'Users', perm: 'users' });
    navItems.push({ path: '/admin/audit-log', label: 'Audit Log', perm: 'audit' });
    navItems.push({ path: '/admin/logs', label: 'Logs', perm: 'admin' });
    navItems.push({ path: '/admin/import-history', label: 'Import History', perm: 'admin' });
    navItems.push({ path: '/admin/test-data', label: 'Test Data Cleanup', perm: 'admin' });
  }

  // Close sidebar when route changes (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // PWA: register the push service worker on admin pages (never on /kiosk)
  useEffect(() => {
    registerSW();
  }, []);

  return (
    <div className="min-h-screen bg-cream-50 flex">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-charcoal-900 text-white px-4 py-3 flex items-center justify-between">
        <button
          onClick={() => setSidebarOpen(true)}
          className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          aria-label="Open menu"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
        <img src="/piczoo-logo.png" alt="Piczoo" className="h-6 object-contain" />
        <div className="w-10" /> {/* Spacer for centering */}
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Desktop always visible, Mobile slide-out */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 bg-charcoal-900 text-white flex flex-col
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="p-6 border-b border-white/10 flex items-center justify-between">
          <div>
            <img src="/piczoo-logo.png" alt="Piczoo" className="h-8 object-contain" />
            <p className="text-sm text-white/60 mt-2">Admin Dashboard</p>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-2 hover:bg-white/10 rounded-lg transition-colors"
            aria-label="Close menu"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <nav className="flex-1 p-4">
          {navItems.map((item) => (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`w-full text-left px-4 py-3 rounded-lg mb-2 transition-all ${
                location.pathname.startsWith(item.path)
                  ? 'bg-white/10 text-white'
                  : 'text-white/60 hover:bg-white/5 hover:text-white'
              }`}
            >
              {item.label}
            </button>
          ))}
        </nav>

        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3 mb-3">
            {user?.picture ? (
              <img
                src={user.picture}
                alt=""
                className="w-8 h-8 rounded-full object-cover border border-white/20"
                referrerPolicy="no-referrer"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-xs uppercase text-white/60">
                {(user?.email || '?').charAt(0)}
              </div>
            )}
            <div className="min-w-0 flex-1">
              {user?.name && (
                <div className="text-sm text-white truncate" data-testid="sidebar-user-name">
                  {user.name}
                </div>
              )}
              <div className="text-xs text-white/50 truncate" data-testid="sidebar-user-email" title={user?.email}>
                {user?.email}
              </div>
            </div>
          </div>
          <PushToggle />
          <button
            onClick={logout}
            className="w-full px-4 py-2 bg-rose-500/20 text-rose-200 rounded-lg hover:bg-rose-500/30 transition-all text-sm"
            data-testid="logout-btn"
          >
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto pt-14 lg:pt-0">
        {children}
      </main>
    </div>
  );
};

function AppRoutes() {
  const location = useLocation();

  // Emergent Google Auth returns via URL fragment #session_id=... — this
  // MUST be checked during render (not in useEffect) so the AuthCallback
  // takes over before ProtectedRoute's own auth checks fire and try to
  // navigate us back to /admin/login.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/admin/login" element={<AdminLogin />} />

      {/* Booth Software Routes — Electron-only (browser visitors get a closed page) */}
      <Route path="/kiosk" element={<BoothOnly><KioskApp /></BoothOnly>} />
      <Route path="/kiosk/:kioskId" element={<BoothOnly><KioskApp /></BoothOnly>} />

      {/* Photo Download Page - Public */}
      <Route path="/download/:sessionId" element={<PhotoDownload />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />

      {/* Partner (revenue-share client) dashboard — scoped to their booths only */}
      <Route path="/partner" element={
        <ProtectedRoute partnerOnly>
          <PartnerDashboard />
        </ProtectedRoute>
      } />

      {/* Admin Routes */}
      <Route path="/admin/dashboard" element={
        <ProtectedRoute requirePermission="dashboard">
          <AdminLayout><Dashboard /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/kiosks" element={
        <ProtectedRoute requirePermission="booths">
          <AdminLayout><KioskList /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/kiosks/:kioskId" element={
        <ProtectedRoute requirePermission="booths">
          <AdminLayout><KioskDetail /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/analytics" element={
        <ProtectedRoute requirePermission="analytics">
          <AdminLayout><AnalyticsDashboard /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/contacts" element={
        <ProtectedRoute requirePermission="leads">
          <AdminLayout><ContactsList /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/users" element={
        <ProtectedRoute requirePermission="users">
          <AdminLayout><AdminUsers /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/software" element={
        <ProtectedRoute requirePermission="software">
          <AdminLayout><SoftwareDownload /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/vouchers" element={
        <ProtectedRoute requirePermission="vouchers">
          <AdminLayout><Vouchers /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/audit-log" element={
        <ProtectedRoute requireSuperAdmin>
          <AdminLayout><AuditLog /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/fleet" element={
        <ProtectedRoute requirePermission="booths">
          <AdminLayout><FleetDashboard /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/late-prints" element={
        <ProtectedRoute requirePermission="payments">
          <AdminLayout><LatePrints /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/payments" element={
        <ProtectedRoute requirePermission="payments">
          <AdminLayout><Payments /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/test-data" element={
        <ProtectedRoute requireSuperAdmin>
          <AdminLayout><TestDataCleanup /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/logs" element={
        <ProtectedRoute requireSuperAdmin>
          <AdminLayout><LogsCenter /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/import-history" element={
        <ProtectedRoute requireSuperAdmin>
          <AdminLayout><ImportHistory /></AdminLayout>
        </ProtectedRoute>
      } />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-center" richColors closeButton toastOptions={{ duration: 3500 }} />
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
